import type { NextPage } from 'next'
import Image from 'next/image'

import {
    fieldGroupsEditingImage,
    fieldGroupsSelectFieldsRedImage,
    tablePipelineImage,
    stacksImage,
    tableEditingCellRedImage,
    tasksBulkEditingImage,
    marblesOnStepsBigImage,
} from 'src/libs/assets'
import { PageLayout } from 'src/libs/layout'
import {
    IntroSection,
    PropositionSection,
    SecuritySection,
    ClosingSection,
    ThreeCardsSection,
    FaqSection,
    BannerWithCtaSection,
    RequestDemoButton,
} from 'src/libs/ui-shared'

const SalesOperationsSolutionPage: NextPage = () => {
    return (
        <PageLayout isNavBarFixed>
            <div className="grid gap-40">
                <IntroSection
                    image={marblesOnStepsBigImage}
                    imageAlt="Marbles falling down steps"
                    label="Sales Operations"
                    title="Create easy to follow processes"
                    description="Make the life of your team easier with simple processes that will help the team to follow the funnel and keep data accurate."
                >
                    <div className="grid place-content-center md:place-content-start">
                        <RequestDemoButton />
                    </div>
                </IntroSection>

                <PropositionSection
                    title="Collaborate with your team in real time"
                    description="Create reports for your teams they can work directly in. Forget about downloading them and sharing them via sheets."
                    image={
                        <Image
                            src={tablePipelineImage}
                            alt="Pipeline table"
                            layout="responsive"
                            placeholder="blur"
                        />
                    }
                    imageShadow
                    swapElementsWhenVertical
                />

                <PropositionSection
                    title="Set rules for better data hygiene"
                    description="Updating records is so smooth, that it seamlessly integrates into the workflow of your team. No need for nagging them to update for forecasts anymore."
                    image={
                        <Image
                            src={fieldGroupsEditingImage}
                            alt="Editing field in field group"
                            layout="responsive"
                            placeholder="blur"
                        />
                    }
                    imageShadow
                />

                <PropositionSection
                    title="Works across objects"
                    description="Laserfocus allows you to combine fields with different objects in the views or customer pages. No need to merge different objects via custom code."
                    image={
                        <Image
                            src={fieldGroupsSelectFieldsRedImage}
                            alt="Selecting fields in field group"
                            layout="responsive"
                            placeholder="blur"
                        />
                    }
                    imageShadow
                    swapElementsWhenVertical
                />

                <BannerWithCtaSection />

                <SecuritySection />

                <ThreeCardsSection
                    title="Set up flexibly and iterate"
                    description="Laserfocus lets you set custom views and for your team, but gives them the flexibility to configure it to their own needs."
                    cards={[
                        {
                            image: (
                                <Image
                                    src={tasksBulkEditingImage}
                                    alt="Bulk-editing for tasks"
                                    layout="responsive"
                                    placeholder="blur"
                                />
                            ),
                            title: 'No more outdated tasks',
                            description:
                                'Bulk edit tasks so you can get a a better overview of what everyone is really working on.',
                            backgroundColor: 'blue',
                        },
                        {
                            image: (
                                <Image
                                    src={stacksImage}
                                    alt="Stacks"
                                    layout="responsive"
                                    placeholder="blur"
                                />
                            ),
                            title: 'Easy to understand',
                            description:
                                'Create work stacks for your team that they can easily follow.',
                            backgroundColor: 'purple',
                        },
                        {
                            image: (
                                <Image
                                    src={tableEditingCellRedImage}
                                    alt="Editing cell in table"
                                    layout="responsive"
                                    placeholder="blur"
                                />
                            ),
                            title: 'Clean CRM data for reports',
                            description:
                                'Updating data is painless in Laserfocus and won’t take hours anymore. Have a clean system to run insightful reports for your org.',
                            backgroundColor: 'red',
                        },
                    ]}
                />

                <FaqSection
                    title="Frequently asked questions"
                    description="Laserfocus helps you become more productive by offering you all the tools you need to close deals with confidence."
                    elements={[
                        {
                            question: 'How can I try Laserfocus for myself?',
                            answer: 'Connect with one click via Salesforce Oauth and start using Laserfocus. Laserfocus sits on top of your Salesforce data, so there is no setup or installation required.',
                        },
                        {
                            question: 'What does Laserfocus cost?',
                            answer: 'It’s free for individuals and small teams trying out Laserfocus.',
                        },
                        {
                            question: 'Do I need my Salesforce Admin for setup?',
                            answer: [
                                'Nope. Laserfocus doesn’t require any setup or installation.',
                                'You securely log in and connect via Salesforce Oauth with Laserfocus and can start using it instantly if your Salesforce license has API access.',
                            ],
                        },
                        {
                            question: 'How long does the setup need?',
                            answer: 'Like everything in Laserfocus: it’s superfast. One click and you are ready. Means 30 seconds.',
                        },
                        {
                            question: 'Are you changing my Salesforce setup?',
                            answer: [
                                'No. Salesforce is defined and setup by your company, we don’t interact with your workflows in Salesforce or create any fields.',
                                'Everything stays untouched in your Salesforce instance.',
                            ],
                        },
                        {
                            question: 'How much time can Laserfocus save me?',
                            answer: 'Glad you asked. Just think about how much time you spend in Salesforce per day and cut that by 80%.',
                        },
                    ]}
                />

                <ClosingSection
                    title="Simplify complex processes now"
                    description="Laserfocus is the modern interface your team will love to use. See how their productivity increases with the tool for perfomers."
                />
            </div>
        </PageLayout>
    )
}

export default SalesOperationsSolutionPage
